import { z } from 'zod';
import {
  AVATAR_IDS,
  AVATAR_IMAGE_MAX_BYTES,
  LEGACY_AVATARS,
  MAX_MATURITY_RANK,
  normalizeAvatar,
} from './constants';

const pinSchema = z.string().regex(/^\d{4}$/, 'PIN must be 4 digits');

// Older apps still send the avatars from before 0.5.18; they become today's nearest one.
const avatarSchema = z
  .string()
  .max(40)
  .refine(
    (id) => (AVATAR_IDS as readonly string[]).includes(id) || Object.hasOwn(LEGACY_AVATARS, id),
    'Choose an avatar',
  )
  .transform(normalizeAvatar);

export const subtitleStyleSchema = z.object({
  size: z.enum(['small', 'medium', 'large', 'xlarge']).default('medium'),
  color: z.enum(['white', 'yellow', 'pink', 'cyan']).default('white'),
  backgroundOpacity: z.number().min(0).max(1).default(0.5),
  position: z.number().min(0).max(30).default(8),
});
export type SubtitleStyle = z.infer<typeof subtitleStyleSchema>;

export const createProfileSchema = z.object({
  name: z.string().trim().min(1, 'Enter a name').max(30, 'Name is too long'),
  avatar: avatarSchema,
  isKids: z.boolean().default(false),
  pin: pinSchema.optional(),
  language: z.string().min(2).max(8).default('en'),
  maturityLimit: z.number().int().min(0).max(MAX_MATURITY_RANK).default(MAX_MATURITY_RANK),
  subtitleLanguage: z.string().min(2).max(8).nullable().default(null),
  subtitlesEnabled: z.boolean().default(false),
});
export type CreateProfileInput = z.input<typeof createProfileSchema>;

export const updateProfileSchema = z.object({
  name: z.string().trim().min(1).max(30).optional(),
  avatar: avatarSchema.optional(),
  isKids: z.boolean().optional(),
  pin: pinSchema.nullable().optional(),
  language: z.string().min(2).max(8).optional(),
  maturityLimit: z.number().int().min(0).max(MAX_MATURITY_RANK).optional(),
  canManageProfiles: z.boolean().optional(),
  accountPassword: z.string().max(200).optional(),
});
export type UpdateProfileInput = z.input<typeof updateProfileSchema>;

export const avatarImageSchema = z.object({
  image: z
    .string()
    .max(Math.ceil((AVATAR_IMAGE_MAX_BYTES * 4) / 3) + 40)
    .regex(
      /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+=*$/,
      'Use a PNG, JPEG or WebP image',
    ),
  accountPassword: z.string().max(200).optional(),
});

export const selectProfileSchema = z.object({ pin: pinSchema.optional() });

export interface Profile {
  id: string;
  name: string;
  avatar: (typeof AVATAR_IDS)[number];
  avatarUrl: string | null;
  isKids: boolean;
  hasPin: boolean;
  isOwner: boolean;
  canManageProfiles: boolean;
  language: string;
  maturityLimit: number;
  /** Restricted by the owner of Tea+: this profile can't use Tea+, other profiles can. */
  restricted: boolean;
  createdAt: string;
}

export interface ProfileAccess {
  ownerId: string | null;
  isOwner: boolean;
  canManage: boolean;
}

/** Heading fonts (Outfit is the Tea+ look; Fraunces was before 0.6.0). All free Google Fonts. */
export const FONT_IDS = [
  'fraunces-classic',
  'playfair',
  'dm-serif',
  'lora',
  'baskerville',
  'space-grotesk',
  'outfit',
  'sora',
  'poppins',
  'bricolage',
  'montserrat',
] as const;

export const THEME_IDS = [
  'midnight',
  'teaplus',
  'plum',
  'graphite',
  'ocean',
  'forest',
  'wine',
  'espresso',
  // Gradient themes: a soft gradient behind everything.
  'aurora',
  'sunset',
  'nebula',
  'lagoon',
  'rosewood',
  'custom-gradient',
  'custom',
] as const;
export const ACCENT_IDS = [
  'pink',
  'lilac',
  'blue',
  'peach',
  'mint',
  'coral',
  'butter',
  'sage',
  'aqua',
  'pearl',
  // Gradient accents: buttons and highlights painted with a gradient.
  'grad-candy',
  'grad-sunrise',
  'grad-ocean',
  'grad-aurora',
  'grad-sorbet',
  'grad-custom',
  'custom',
] as const;
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a colour like #1b2a4a');

/** The kinds of notification a profile can set up one by one. */
export const NOTIFICATION_KINDS = ['chat', 'shared', 'watching', 'friends', 'party'] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export const notificationRuleSchema = z.object({
  show: z.boolean(),
  /** Also while the player is open. */
  whilePlaying: z.boolean(),
  sound: z.boolean(),
});
export type NotificationRule = z.infer<typeof notificationRuleSchema>;

export const preferencesSchema = z.object({
  preferredQuality: z.enum(['auto', '4K', '1080p', '720p', '480p']).default('auto'),
  preferredAudioLanguage: z.string().max(8).nullable().default(null),
  preferredSubtitleLanguage: z.string().max(8).nullable().default(null),
  subtitlesEnabled: z.boolean().default(false),
  subtitleStyle: subtitleStyleSchema.default({
    size: 'medium',
    color: 'white',
    backgroundOpacity: 0.5,
    position: 8,
  }),
  autoplayNextEpisode: z.boolean().default(true),
  /** Jump past intros and recaps by themselves when Tea+ knows where they are. */
  autoSkipIntros: z.boolean().default(false),
  resumeBehavior: z.enum(['ask', 'resume', 'restart']).default('ask'),
  playbackSpeed: z.number().min(0.25).max(3).default(1),
  preferredCodec: z.enum(['any', 'H.264', 'HEVC', 'AV1']).default('any'),
  preferredAddonId: z.string().max(200).nullable().default(null),
  streamSort: z.enum(['quality', 'size', 'addon', 'availability']).default('quality'),
  saveSearchHistory: z.boolean().default(true),
  theme: z.enum(THEME_IDS).default('graphite'),
  accent: z.enum(ACCENT_IDS).default('aqua'),
  customTheme: hexColor.default('#1b2a4a'),
  customAccent: hexColor.default('#f4a7c5'),
  /** The two colours of the custom gradient theme and gradient accent. */
  customThemeGradient: z.tuple([hexColor, hexColor]).default(['#123a3a', '#2a1440']),
  customAccentGradient: z.tuple([hexColor, hexColor]).default(['#f4a7c5', '#a7c8f4']),
  // "fraunces" was the default before 0.6.0, saved with every profile, so it means the default now.
  font: z.preprocess((v) => (v === 'fraunces' ? 'outfit' : v), z.enum(FONT_IDS)).default('outfit'),
  videoFit: z.enum(['fit', 'fill', 'stretch']).default('fit'),
  reducedMotion: z.enum(['system', 'on', 'off']).default('system'),
  density: z.enum(['comfortable', 'compact']).default('comfortable'),
  shareActivity: z.boolean().default(false),
  /** Show what this profile is doing on Discord (Rich Presence, in the Windows app). */
  discordPresence: z.boolean().default(false),
  friendsEnabled: z.boolean().default(true),
  /** Off: friends can't message this profile and it has no Chats tab. Messages are kept. */
  chatEnabled: z.boolean().default(true),
  notificationsEnabled: z.boolean().default(true),
  notificationSounds: z.boolean().default(true),
  /** What each kind of notification does. A kind with no rule follows the two settings below. */
  // Kinds it doesn't know (from a newer app, or one that was taken out) are dropped, rather than
  // failing the whole preferences object and resetting the profile's settings.
  notificationRules: z
    .preprocess(
      (v) =>
        v && typeof v === 'object'
          ? Object.fromEntries(
              Object.entries(v).filter(([k]) =>
                (NOTIFICATION_KINDS as readonly string[]).includes(k),
              ),
            )
          : v,
      z.partialRecord(z.enum(NOTIFICATION_KINDS), notificationRuleSchema),
    )
    .default({}),
  /** Older setting: keep notifications away while the player is open. Party requests still show... */
  hideNotificationsDuringPlayback: z.boolean().default(false),
  /** ...unless this is on too. */
  hidePartyRequestsDuringPlayback: z.boolean().default(false),
  ratingsEnabled: z.boolean().default(true),
  privateTitles: z.array(z.string().max(64)).max(1000).default([]),
});
export type Preferences = z.infer<typeof preferencesSchema>;
export const updatePreferencesSchema = preferencesSchema.partial();
export type UpdatePreferencesInput = z.input<typeof updatePreferencesSchema>;
/** A kind's rule. Until it has its own, it does what the older playback settings said. */
export function notificationRule(
  prefs: Pick<
    Preferences,
    'notificationRules' | 'hideNotificationsDuringPlayback' | 'hidePartyRequestsDuringPlayback'
  >,
  kind: NotificationKind,
): NotificationRule {
  return (
    prefs.notificationRules[kind] ?? {
      show: true,
      whilePlaying:
        !prefs.hideNotificationsDuringPlayback ||
        (kind === 'party' && !prefs.hidePartyRequestsDuringPlayback),
      sound: true,
    }
  );
}

export const DEFAULT_PREFERENCES: Preferences = preferencesSchema.parse({});
